/**
 * Theme Colour Resolution Utility
 *
 * Handles v1 (raw hex strings) and v2 (source-referenced) theme formats.
 * v2 colour fields store { source: "primary" } or { source: "custom", value: "#hex" }
 * which are resolved against the org's brand colours at render time.
 */

const COLOUR_FIELDS = ['card_bg', 'card_text', 'button_bg', 'button_text'];
const BRAND_SOURCES = ['primary', 'secondary', 'tertiary'];

/**
 * Apply a colour shift to a brand source name.
 * Shift 0 = no change, shift 1 = primary→secondary→tertiary→primary, etc.
 *
 * @param {string} source - 'primary', 'secondary', 'tertiary', or 'custom'
 * @param {number} shift - 0, 1, or 2
 * @returns {string} Shifted source name
 */
export function shiftSource(source, shift) {
    if (!shift || source === 'custom') return source;
    const idx = BRAND_SOURCES.indexOf(source);
    if (idx === -1) return source;
    return BRAND_SOURCES[(idx + shift) % BRAND_SOURCES.length];
}

/**
 * Check whether a theme content object is v2 format.
 */
export function isV2Theme(content) {
    return content?.version === 2;
}

/**
 * Resolve a single colour field to a hex string.
 *
 * - v1 string values pass through unchanged
 * - v2 objects resolve against brand colours
 *
 * @param {string|Object} field - Raw hex string (v1) or { source, value? } object (v2)
 * @param {Object|null} brand - { colour, colour_secondary, colour_tertiary }
 * @param {number} shift - Colour shift (0, 1, or 2)
 * @returns {string} Resolved hex colour string
 */
export function resolveColourValue(field, brand, shift = 0) {
    if (typeof field === 'string') return field;
    if (!field || typeof field !== 'object') return '';

    const source = shiftSource(field.source, shift);

    switch (source) {
    case 'primary':
        return brand?.colour || field.value || '#E55E2C';
    case 'secondary':
        return brand?.colour_secondary || brand?.colour || field.value || '#E55E2C';
    case 'tertiary':
        return brand?.colour_tertiary || brand?.colour || field.value || '#E55E2C';
    case 'custom':
        return field.value || '#E55E2C';
    default:
        return field.value || '';
    }
}

/**
 * Resolve all colour fields in a theme content object to flat hex strings.
 *
 * v1 content passes through unchanged. v2 content is resolved against brand,
 * and button_hover_bg / button_hover_text are derived by swapping button colours.
 *
 * @param {Object} content - Theme content object (v1 or v2)
 * @param {Object|null} brand - Brand colours
 * @param {number} shift - Colour shift (0, 1, or 2)
 * @returns {Object} Resolved content with all colour values as hex strings
 */
export function resolveThemeContent(content, brand, shift = 0) {
    if (!content) return content;
    if (!isV2Theme(content)) return content;

    const resolved = {};

    for (const [key, value] of Object.entries(content)) {
        if (key === 'version') continue;
        if (key === 'button_hover_bg' || key === 'button_hover_text') continue;

        if (key === 'background_data') {
            resolved[key] = resolveBackgroundData(value, brand, shift);
        } else if (COLOUR_FIELDS.includes(key)) {
            resolved[key] = resolveColourValue(value, brand, shift);
        } else {
            resolved[key] = value;
        }
    }

    // Derive hover states by swapping button colours
    resolved.button_hover_bg = resolved.button_text || '#FFFFFF';
    resolved.button_hover_text = resolved.button_bg || '#E55E2C';

    return resolved;
}

/**
 * Resolve gradient stop sources within background_data.
 *
 * @param {Object|null} backgroundData - Background data object
 * @param {Object|null} brand - Brand colours
 * @param {number} shift - Colour shift (0, 1, or 2)
 * @returns {Object|null} Background data with resolved gradient hex values
 */
/**
 * Resolve a gradient stop's hex value from its brand source,
 * preserving the alpha channel from the existing hex.
 */
function resolveStopHex(stop, brand, shift) {
    const brandHex = resolveColourValue({ source: stop.source }, brand, shift);
    // Extract alpha from existing hex if present (8-char hex = 6 colour + 2 alpha)
    const existingHex = (stop.hex || '').replace('#', '');
    if (existingHex.length === 8) {
        const alpha = existingHex.slice(6, 8);
        return brandHex + alpha;
    }
    return brandHex;
}

export function resolveBackgroundData(backgroundData, brand, shift = 0) {
    if (!backgroundData) return backgroundData;

    const resolved = { ...backgroundData };

    // Resolve gradient stop colours
    if (resolved.colours) {
        resolved.colours = resolved.colours.map(stop => {
            if (stop.source && stop.source !== 'custom') {
                return {
                    ...stop,
                    hex: resolveStopHex(stop, brand, shift),
                };
            }
            return stop;
        });
    }

    // Resolve hybrid overlay gradient stops
    if (resolved.overlay?.colours) {
        resolved.overlay = {
            ...resolved.overlay,
            colours: resolved.overlay.colours.map(stop => {
                if (stop.source && stop.source !== 'custom') {
                    return {
                        ...stop,
                        hex: resolveStopHex(stop, brand, shift),
                    };
                }
                return stop;
            }),
        };
    }

    // Resolve solid background colour source
    if (resolved.type === 'solid' && resolved.colourSource && resolved.colourSource !== 'custom') {
        resolved.colour = resolveColourValue({ source: resolved.colourSource }, brand, shift);
    }

    // Resolve hybrid overlay solid colour source
    if (resolved.overlay?.type === 'solid' && resolved.overlay?.colourSource && resolved.overlay.colourSource !== 'custom') {
        resolved.overlay = {
            ...resolved.overlay,
            colour: resolveColourValue({ source: resolved.overlay.colourSource }, brand, shift),
        };
    }

    return resolved;
}

/**
 * Match a hex colour against brand colours and return a v2 source object.
 *
 * @param {string} hex - Hex colour to match
 * @param {Object} brand - Brand colours
 * @returns {Object} v2 colour source object
 */
export function hexToSource(hex, brand) {
    if (!hex || !brand) return { source: 'custom', value: hex || '#E55E2C' };

    const normalised = hex.toLowerCase();

    if (brand.colour && normalised === brand.colour.toLowerCase()) {
        return { source: 'primary' };
    }
    if (brand.colour_secondary && normalised === brand.colour_secondary.toLowerCase()) {
        return { source: 'secondary' };
    }
    if (brand.colour_tertiary && normalised === brand.colour_tertiary.toLowerCase()) {
        return { source: 'tertiary' };
    }

    return { source: 'custom', value: hex };
}

/**
 * Determine which colour source is active in a brand colour picker.
 *
 * When the custom picker is open the source is always 'custom' — otherwise a
 * custom-selected hex that happens to equal a brand colour would round-trip
 * through hexToSource back to that brand source, snapping the picker shut.
 *
 * @param {string|Object} colourField - Raw hex string (v1) or { source, value? } object (v2)
 * @param {boolean} customPickerOpen - Whether the custom colour picker is open
 * @returns {string} Active source: 'primary', 'secondary', 'tertiary', or 'custom'
 */
export function activeColourSource(colourField, customPickerOpen) {
    if (customPickerOpen) {
        return 'custom';
    }
    if (typeof colourField === 'string') {
        return 'custom';
    }
    return colourField?.source || 'custom';
}

/**
 * Convert a v1 theme content object to v2 format.
 *
 * Matches existing hex values against brand colours where possible.
 * Drops button_hover_bg and button_hover_text (derived in v2).
 *
 * @param {Object} content - v1 theme content
 * @param {Object} brand - Brand colours for matching
 * @returns {Object} v2 theme content
 */
export function convertV1ToV2(content, brand) {
    if (!content) return content;
    if (isV2Theme(content)) return content;

    const v2 = { version: 2 };

    for (const [key, value] of Object.entries(content)) {
        if (key === 'button_hover_bg' || key === 'button_hover_text') continue;

        if (COLOUR_FIELDS.includes(key) && typeof value === 'string') {
            v2[key] = hexToSource(value, brand);
        } else if (key === 'background_data' && value) {
            v2[key] = convertBackgroundDataToV2(value, brand);
        } else {
            v2[key] = value;
        }
    }

    return v2;
}

/**
 * Add source fields to gradient stops in background_data.
 */
function convertBackgroundDataToV2(backgroundData, brand) {
    if (!backgroundData) return backgroundData;

    const converted = { ...backgroundData };

    if (converted.colours) {
        converted.colours = converted.colours.map(stop => ({
            ...stop,
            source: hexToSource(stop.hex, brand).source === 'custom'
                ? 'custom'
                : hexToSource(stop.hex, brand).source,
        }));
    }

    if (converted.overlay?.colours) {
        converted.overlay = {
            ...converted.overlay,
            colours: converted.overlay.colours.map(stop => ({
                ...stop,
                source: hexToSource(stop.hex, brand).source === 'custom'
                    ? 'custom'
                    : hexToSource(stop.hex, brand).source,
            })),
        };
    }

    return converted;
}
